import type { Identity } from '../types/auth';

/**
 * Modpack config permission nodes, mirroring what argus-backend gates on
 * (src/auth.ts, `modpack_config_*`).
 *
 * Deliberately separate from the `argus_modpack_*` groups, which gate modpack
 * *notes*: someone trusted to write a note is not thereby trusted to change
 * the address a live server is reached on.
 *
 * `write` and `delete` each imply `read` — nobody can sensibly change a config
 * they cannot see. `admin` grants all four, and `SPY_Super_admin` keeps the
 * usual Argus override.
 *
 * Client-side checks are for UX only — the server validates every mutation.
 */

export const MODPACK_CONFIG_PERMISSIONS = {
  read: 'argus.modpackconfigs.read',
  write: 'argus.modpackconfigs.write',
  delete: 'argus.modpackconfigs.delete',
  admin: 'argus.modpackconfigs.admin',
} as const;

export type ModpackConfigCapability = keyof typeof MODPACK_CONFIG_PERMISSIONS;

export interface ModpackConfigPerms {
  /** View configs, in both the production and dev tables. */
  canView: boolean;
  /** Create a config, edit ordinary fields, bump a version across a cluster. */
  canWrite: boolean;
  /** Delete a config, including promoting a successor when it heads a cluster. */
  canDelete: boolean;
  /**
   * The protected network/identity fields, the dev sync, and the Discord-side
   * provisioning and announcement actions.
   */
  canAdmin: boolean;
}

function nodes(identity: Identity | null | undefined): string[] {
  const permissions = identity?.raw?.custom?.permissions;
  return Array.isArray(permissions) ? permissions : [];
}

function isSuper(identity: Identity | null | undefined): boolean {
  const groups = identity?.raw?.custom?.groups;
  return Array.isArray(groups) && groups.includes('SPY_Super_admin');
}

function isUpdateAdministrator(identity: Identity | null | undefined): boolean {
  const teams = identity?.raw?.custom?.staff_teams;
  return Array.isArray(teams) && teams.some(
    team => typeof team === 'string' && team.trim().toLowerCase() === 'administrator',
  );
}

/** Guided updates, including cluster jobs, confirmations, uploads and recovery. */
export function canRunModpackUpdates(identity: Identity | null | undefined): boolean {
  return isUpdateAdministrator(identity) || hasModpackConfigCapability(identity, 'write');
}

/** Migrations move a live server's network identity and require the pack-manager config grant. */
export function canRunModpackMigrations(identity: Identity | null | undefined): boolean {
  return hasModpackConfigCapability(identity, 'write');
}

export function hasModpackConfigCapability(
  identity: Identity | null | undefined,
  capability: ModpackConfigCapability,
): boolean {
  if (isSuper(identity)) return true;
  const held = nodes(identity);
  if (held.includes(MODPACK_CONFIG_PERMISSIONS.admin)) return true;
  if (held.includes(MODPACK_CONFIG_PERMISSIONS[capability])) return true;
  // Reading is implied by the narrower grants.
  return capability === 'read'
    && (isUpdateAdministrator(identity) || held.includes(MODPACK_CONFIG_PERMISSIONS.write) || held.includes(MODPACK_CONFIG_PERMISSIONS.delete));
}

export function getModpackConfigPerms(
  identity: Identity | null | undefined,
  isDev: boolean,
): ModpackConfigPerms {
  if (isDev) {
    return { canView: true, canWrite: true, canDelete: true, canAdmin: true };
  }
  return {
    canView: hasModpackConfigCapability(identity, 'read'),
    canWrite: hasModpackConfigCapability(identity, 'write'),
    canDelete: hasModpackConfigCapability(identity, 'delete'),
    canAdmin: hasModpackConfigCapability(identity, 'admin'),
  };
}

/** Shared by the route guard in App.tsx and the sidebar entry in navigation.ts. */
export const canViewModpackConfigs = (identity: Identity | null | undefined): boolean =>
  hasModpackConfigCapability(identity, 'read');
